import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import type { JSX } from 'react';
import { useFormContext } from 'react-hook-form';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import type { User } from '~hooks/useUsers/models';
import useUsers from '~hooks/useUsers/useUsers';

import {
  type OrderFormV3FieldValues,
  useOrderFormV3Context,
} from '../OrderFormV3Context';

/**
 * Renders the requester card. Render-only: the company-scoped users
 * typeahead. The request scope is the Provider's company watch
 * (context.watchedCompany — never a local watch); the fetcher is the same
 * construction-silent useUsers() factory legacy reads — it fires only when
 * the dropdown opens, holds no state, and needs no Provider wiring (the
 * company id rides per-request via extraRequestOptions).
 */
export function RequesterPanel(): JSX.Element {
  const { watchedCompany } = useOrderFormV3Context();
  const {
    control,
    formState: { errors },
  } = useFormContext<OrderFormV3FieldValues>();
  const { getCompanyUsersTypeahead } = useUsers();

  return (
    <Card sx={{ p: 2, my: 2 }}>
      <Typography variant="form-section-title" sx={{ mb: 1 }}>
        {t('order.form.requester')}
      </Typography>
      {/* Test-id anchor (orderFieldMetadata.requester.testId): a bare Box —
          the field renders a block-level fullWidth FormControl, so a plain
          div wrapper preserves the legacy layout (matches the TrucksPanel checkbox precedent). */}
      <Box data-test-id="order-form-v3-requester">
        <AutocompleteAsyncFormField
          control={control}
          errors={errors}
          name="requester"
          isRequired={false}
          clearable={true}
          getValue={(user: User) => user.id}
          getLabel={(user: User) => user.fullName ?? ''}
          asyncCallback={getCompanyUsersTypeahead}
          extraRequestOptions={{ companyId: watchedCompany?.id }}
          hideLabel
        />
      </Box>
    </Card>
  );
}
